import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | The APN - Accelerated Professionals Network</title>
        <meta name="description" content="Privacy Policy for The APN (Accelerated Professionals Network) - How we collect, use, and protect your personal information." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow-lg rounded-lg p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">
              Privacy Policy
            </h1>
            
            <div className="prose max-w-none">
              <p className="mb-6 text-lg text-gray-700">
                The Accelerated Professionals Network (APN) is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, and safeguard your data.
              </p>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4">ARTICLE II - PRIVACY POLICY</h2>
              
              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.1 Categories of Data Collected</h3>
              <p className="mb-4">
                APN may collect personal data including but not limited to name, address, email, phone number, employer, job title, payment details, event attendance records, and communications with APN.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.2 Lawful Basis for Processing</h3>
              <p className="mb-4">
                Personal data is collected and processed under lawful bases including consent, contractual necessity, legal obligation, and APN's legitimate interests in managing its membership and events.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.3 Use of Data</h3>
              <p className="mb-4">
                Data may be used to administer memberships, process payments, register event participants, communicate with members, maintain records, and comply with applicable laws.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.4 Retention of Data</h3>
              <p className="mb-4">
                APN retains personal data for as long as necessary to fulfill the purposes for which it was collected, to comply with legal obligations, or to enforce rights. Data that is no longer required shall be securely deleted or anonymized.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.5 Security of Data</h3>
              <p className="mb-4">
                APN implements commercially reasonable safeguards to protect data, but cannot guarantee absolute security. Members acknowledge that transmission of data over the internet carries inherent risks.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.6 Sharing of Data</h3>
              <p className="mb-4">
                Data may be shared with vendors, service providers, payment processors, and event partners as necessary to fulfill APN's purposes. APN does not sell personal data.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.7 Cross-Border Transfers</h3>
              <p className="mb-4">
                Data may be transferred outside of the jurisdiction of collection. APN shall take reasonable steps to ensure adequate protection consistent with applicable data protection laws.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.8 Member Rights</h3>
              <p className="mb-4">
                Members may request access to, correction of, or deletion of their personal data. Where applicable under GDPR or CCPA, members may exercise rights including portability, restriction of processing, and objection.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.9 Breach Notification</h3>
              <p className="mb-4">
                In the event of a data breach that may compromise personal data, APN shall notify affected members as required by law.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.10 Indemnification and Limitation of Liability</h3>
              <p className="mb-4">
                Members shall indemnify and hold harmless APN and its Officers and Agents from claims arising from misuse of APN's systems or member-provided data. APN's liability is limited to dues paid in the twelve months preceding the claim.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.11 Dispute Resolution</h3>
              <p className="mb-4">
                This Article shall be governed by New York law. Disputes shall be mediated in New York and, if unresolved, resolved by binding arbitration under the rules of the American Arbitration Association.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">II.12 Hierarchy</h3>
              <p className="mb-4">
                In case of conflict, the Membership Agreement shall control.
              </p>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Contact Information</h2>
              <p className="mb-4">
                If you have questions about this Privacy Policy or wish to exercise your rights regarding your personal data, please contact us at:
              </p>
              <div className="bg-gray-100 p-4 rounded-lg mb-6">
                <p className="mb-2"><strong>The APN - Accelerated Professionals Network</strong></p>
                <p className="mb-2">Email: privacy@the-apn.com</p>
                <p className="mb-2">Website: https://the-apn.com/about/contact</p>
              </div>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Your Rights</h2>
              <p className="mb-4">
                Depending on your location, you may have the following rights regarding your personal data:
              </p>
              <ul className="list-disc pl-6 mb-6">
                <li className="mb-2"><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
                <li className="mb-2"><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                <li className="mb-2"><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li className="mb-2"><strong>Portability:</strong> Request transfer of your data to another organization</li>
                <li className="mb-2"><strong>Restriction:</strong> Request limitation of processing of your data</li>
                <li className="mb-2"><strong>Objection:</strong> Object to processing of your data for certain purposes</li>
              </ul>

              <div className="mt-8 p-4 bg-blue-50 border-l-4 border-blue-400">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Important Notice</h3>
                <p className="text-blue-700">
                  By using APN services, registering for events, or becoming a member, you acknowledge that you have read and understood this Privacy Policy and consent to the collection, use, and processing of your personal data as described herein.
                </p>
              </div>

              <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Version:</strong> August 29, 2025 – Master Copy<br />
                  <strong>Last Updated:</strong> {new Date().toLocaleDateString()}<br />
                  <strong>Effective Date:</strong> {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
